import { useCallback, useEffect, useState, type JSX } from 'react';

import type { EligibilityCheckResult } from '../../lib/ffs-api.js';

/**
 * How long Retry stays disabled after a press, in ms.
 *
 * The server already asks the member to "try again in a few minutes"; this is what makes the button
 * agree with that sentence. Every press costs a billable payer transaction AND a full cross-program
 * search, and a rapid burst is itself a plausible cause of the rejection it is retrying — the
 * 2026-09-17 prod burst put 12 requests through in 38 seconds, which is the shape of traffic a payer
 * rate-limits. Deliberately short enough not to feel broken: the point is to break the reflex, not
 * to lock the member out of a screen.
 */
export const RETRY_COOLDOWN_MS = 15_000;

function dollars(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

/**
 * Self-pay price formatter ONLY — the spec's copy table writes the self-pay sentence as
 * `Initial one-hour consults are $100.`, a whole-dollar amount with no cents. Drops the `.00` when
 * the price is a whole number of dollars, but keeps the decimals otherwise (a `$99.50` price still
 * renders correctly) — this truncates trailing zero cents, it does not truncate the price itself.
 * Do NOT use this for the copay sentence: that spec entry is written as the variable `$X` and its
 * test pins `$25.00`, so copay keeps full `dollars()` formatting — a copay genuinely can carry
 * cents that must be shown.
 */
function selfPayDollars(cents: number): string {
  return cents % 100 === 0 ? `$${cents / 100}` : dollars(cents);
}

/**
 * Closing line offering the $100 self-pay rate, shared verbatim by all three self-pay-eligible
 * states (out-of-pocket, member-not-found, chosen self-pay). The figure is server-owned
 * (`selfPayPriceCents`, Tasks 2/3) — there is exactly one owner for a price quoted to members and
 * eventually charged, so this never falls back to a hardcoded literal. Missing at render time ⇒
 * the sentence renders without a figure rather than lying about the price.
 */
function selfPayOfferSentence(selfPayPriceCents: number | undefined): string {
  return selfPayPriceCents !== undefined
    ? `Initial one-hour consults are ${selfPayDollars(selfPayPriceCents)}.`
    : 'Initial one-hour consults are available at our self-pay rate.';
}

/**
 * Everything `EligibilityCheckResult` can be EXCEPT `existing_member` — that result is a terminal
 * cross-program block handled at the page level (`ExistingMemberSupport`), so it must never reach
 * this component. Narrowing the prop type (rather than adding a branch here) makes that invariant
 * a compile error instead of a runtime one.
 */
export type NonExistingMemberResult = Exclude<EligibilityCheckResult, { kind: 'existing_member' }>;

/**
 * Its own component because it holds state: the parent renders from a result object and has no
 * business owning a timer.
 *
 * Self-pay is deliberately NOT gated by the cooldown — it is the way off this screen, and a member
 * who has decided to pay should never be made to wait for a payer they have given up on.
 */
function PayerUnavailableOutcome({
  message,
  canProceedOutOfPocket,
  onRetry,
  onSelfPay,
  outline,
}: {
  message: string;
  canProceedOutOfPocket: boolean;
  onRetry: () => void;
  onSelfPay: () => void;
  outline: string;
}): JSX.Element {
  const [coolingDown, setCoolingDown] = useState(false);

  useEffect(() => {
    if (!coolingDown) return undefined;
    const timer = setTimeout(() => setCoolingDown(false), RETRY_COOLDOWN_MS);
    return () => clearTimeout(timer);
  }, [coolingDown]);

  const retry = useCallback(() => {
    if (coolingDown) return;
    setCoolingDown(true);
    onRetry();
  }, [coolingDown, onRetry]);

  return (
    <div className="flex flex-col gap-4">
      {/* The server owns this sentence — it is the one that says how long to wait. */}
      <p className="text-[var(--ink)]">{message}</p>
      <div className="flex flex-wrap gap-3">
        <button
          className={`${outline} disabled:cursor-not-allowed disabled:opacity-50`}
          type="button"
          onClick={retry}
          disabled={coolingDown}
        >
          Retry
        </button>
        {canProceedOutOfPocket && (
          <button
            className="nrx-cta rounded-full px-6 py-3 font-semibold"
            type="button"
            onClick={onSelfPay}
          >
            Continue without insurance
          </button>
        )}
      </div>
    </div>
  );
}

export function EligibilityOutcome({
  result,
  onEnroll,
  onRetry,
  onReset,
  onSelfPay,
  planLabel,
  memberId,
}: {
  result: NonExistingMemberResult;
  onEnroll: () => void;
  onRetry: () => void;
  onReset: () => void;
  /**
   * Take the self-pay offer from a state where no coverage was established — the 503 outage
   * (`payer_unavailable`) and `member_not_found`.
   *
   * Deliberately NOT `onEnroll`. Enrolling from here would submit the member's INSURANCE payerId
   * and memberId against a plan that was never verified, and `requiresPayment()` is false for
   * `payer_unavailable` — so the member would skip card capture and book a self-pay consult for
   * free. The page re-runs the eligibility check as self-pay instead, which yields a `self_pay`
   * result carrying the price and routes through the ordinary (working) payment path.
   */
  onSelfPay: () => void;
  /** Display label for the selected plan (e.g. `BCBS NC- HMO- Blue Care`), looked up from
   *  `SUPPORTED_PAYERS` by the stored `groupId`. Absent for self-pay — no plan was checked. */
  planLabel?: string;
  /** The member id submitted on the form. Absent for self-pay — there is no member id to show. */
  memberId?: string;
}): JSX.Element {
  const enrollCta = (
    <button
      className="nrx-cta rounded-full px-6 py-3.5 text-base font-semibold"
      type="button"
      onClick={onEnroll}
    >
      Enroll &amp; schedule my first consult →
    </button>
  );

  if (result.kind === 'eligible') {
    const covered = result.costPath !== 'out-of-pocket';
    const accent = covered ? 'var(--chip-green)' : 'var(--chip-orange)';

    let messages: string[];
    if (result.costPath === 'covered') {
      messages = ['NourishedRx consults are fully covered by your insurance.'];
    } else if (result.costPath === 'copay') {
      messages = [
        result.copayAmountCents !== undefined
          ? `NourishedRx consults are covered by your insurance, and your copay is ${dollars(result.copayAmountCents)}.`
          : 'NourishedRx consults are covered by your insurance.',
      ];
    } else {
      // out-of-pocket: found the member, but their plan does not cover this — distinct from
      // member_not_found below, which never matched them at all. Do NOT collapse these two.
      messages = [
        ...(planLabel !== undefined && memberId !== undefined
          ? [
              `We checked your ${planLabel} with Member ID ${memberId}. Your plan does not cover nutrition consults.`,
            ]
          : []),
        `NourishedRx consults are not covered by your insurance, but self-pay is available to you. ${selfPayOfferSentence(result.selfPayPriceCents)}`,
      ];
    }

    return (
      <div className="flex flex-col gap-5">
        <div className="flex items-start gap-3">
          <span
            className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-base font-bold text-white"
            style={{ backgroundColor: accent }}
            aria-hidden="true"
          >
            {covered ? '✓' : '$'}
          </span>
          <div className="flex flex-col gap-2">
            {messages.map((message) => (
              <p key={message} className="text-lg leading-relaxed text-[var(--ink)]">
                {message}
              </p>
            ))}
          </div>
        </div>
        {enrollCta}
      </div>
    );
  }

  const outline =
    'rounded-full border border-[var(--color-field-border)] px-6 py-3 font-medium text-[var(--forest)] hover:bg-[var(--forest-5)]';

  if (result.kind === 'payer_unavailable') {
    return (
      <PayerUnavailableOutcome
        message={result.message}
        canProceedOutOfPocket={result.canProceedOutOfPocket}
        onRetry={onRetry}
        onSelfPay={onSelfPay}
        outline={outline}
      />
    );
  }

  if (result.kind === 'member_not_found') {
    // Distinct from the out-of-pocket branch above: we never matched this member, so this must
    // NOT claim their plan doesn't cover the visit — that would be false, and would send someone
    // who typed everything correctly back to re-enter details that were already right.
    // Order matters (spec: not-found sentence → Made a mistake? → then the self-pay offer): a
    // mismatched member is usually a typo, so the fix-it affordance belongs before the self-pay
    // pitch, not after it.
    return (
      <div className="flex flex-col gap-4">
        {planLabel !== undefined && memberId !== undefined && (
          <p className="text-lg leading-relaxed text-[var(--ink)]">
            We checked your {planLabel} with Member ID {memberId} and did not find your information.
          </p>
        )}
        <button className={outline} type="button" onClick={onReset}>
          Made a mistake?
        </button>
        <p className="text-lg leading-relaxed text-[var(--ink)]">
          NourishedRx consults are not covered by your insurance, but self-pay is available to you.{' '}
          {selfPayOfferSentence(result.selfPayPriceCents)}
        </p>
        {/* The offer above was copy-only until now: this screen stated self-pay was available and
            gave no way to accept it, so the only exit was "Made a mistake?" back to the form. */}
        <button
          className="nrx-cta rounded-full px-6 py-3 font-semibold"
          type="button"
          onClick={onSelfPay}
        >
          Continue without insurance
        </button>
      </div>
    );
  }

  if (result.kind === 'self_pay') {
    // No payer was checked (the member chose self-pay), so no coverage claim belongs here — only
    // the price, and the CTA that lets them actually take the offer.
    return (
      <div className="flex flex-col gap-5">
        <div className="flex items-start gap-3">
          <span
            className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-base font-bold text-white"
            style={{ backgroundColor: 'var(--chip-orange)' }}
            aria-hidden="true"
          >
            $
          </span>
          <p className="text-lg leading-relaxed text-[var(--ink)]">
            {selfPayOfferSentence(result.selfPayPriceCents)}
          </p>
        </div>
        {enrollCta}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <p className="text-[var(--ink)]">{result.message}</p>
      <button className={outline} type="button" onClick={onRetry}>
        Try again
      </button>
    </div>
  );
}
